// @ts-check
/** @typedef {import('../types/api').VendorOrder} VendorOrder */
/** @typedef {import('../types/api').Vendor} Vendor */
/** @typedef {import('../types/api').VendorInvoice} VendorInvoice */
/** @typedef {import('../types/api').VendorPayment} VendorPayment */
/** @typedef {import('../types/api').QualityIssue} QualityIssue */

import { AlertCircle, ArrowLeft, Copy, DollarSign, FileText, Package, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import vendorService from '../services/vendorService';

const VendorOrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  /** @type {[VendorOrder | null, Function]} */
  const [order, setOrder] = useState(null);
  /** @type {[Vendor | null, Function]} */
  const [vendor, setVendor] = useState(null);
  /** @type {[VendorInvoice[], Function]} */
  const [invoices, setInvoices] = useState([]);
  /** @type {[VendorPayment[], Function]} */
  const [payments, setPayments] = useState([]);
  /** @type {[QualityIssue[], Function]} */
  const [qualityIssues, setQualityIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQualityIssueModal, setShowQualityIssueModal] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const orderData = await vendorService.getVendorOrder(orderId);
      setOrder(orderData);

      const vendorData = await vendorService.getVendor(orderData.vendor_id);
      setVendor(vendorData);

      const [invoicesData, paymentsData, issuesData] = await Promise.all([
        vendorService.getVendorInvoices({ vendor_order_id: orderId }),
        vendorService.getVendorPayments({ vendor_order_id: orderId }),
        vendorService.getQualityIssues({ vendor_order_id: orderId }),
      ]);

      setInvoices(invoicesData);
      setPayments(paymentsData);
      setQualityIssues(issuesData);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [orderId, showToast]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-muted text-foreground',
      sent: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      confirmed: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-300',
      in_production: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
      ready_for_pickup: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
      delivered: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      completed: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!order) {
    return (
      <div>
        <p className="text-center text-muted-foreground">Order not found</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/vendors/orders')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft size={20} />
          Back to Orders
        </button>

        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-foreground">{order.order_number}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                {order.status.replace('_', ' ')}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  order.priority === 'urgent'
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                    : order.priority === 'high'
                      ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
                      : order.priority === 'medium'
                        ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                        : 'bg-muted text-foreground'
                }`}
              >
                {order.priority}
              </span>
            </div>
            <p className="text-muted-foreground">Vendor: {vendor?.vendor_name}</p>
            <p className="text-sm text-muted-foreground">
              Order Date: {new Date(order.order_date).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={() => navigate(`/vendors/orders/create?duplicate=${order.id}`)}
            className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 transition text-sm"
            title="Start a new order pre-filled from this one"
          >
            <Copy size={16} />
            Duplicate Order
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-card rounded-lg shadow p-4">
          <p className="text-sm text-muted-foreground mb-1">Order Total</p>
          <p className="text-2xl font-bold text-foreground">
            ₹{parseFloat(String(order.total_amount)).toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-card rounded-lg shadow p-4">
          <p className="text-sm text-muted-foreground mb-1">Invoices</p>
          <p className="text-2xl font-bold text-foreground">{invoices.length}</p>
        </div>
        <div className="bg-card rounded-lg shadow p-4">
          <p className="text-sm text-muted-foreground mb-1">Payments</p>
          <p className="text-2xl font-bold text-foreground">{payments.length}</p>
        </div>
        <div className="bg-card rounded-lg shadow p-4">
          <p className="text-sm text-muted-foreground mb-1">Quality Issues</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{qualityIssues.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card rounded-lg shadow overflow-hidden">
        <div className="flex border-b overflow-x-auto">
          {[
            { key: 'details', label: 'Order Details', icon: Package },
            { key: 'invoices', label: 'Invoices', icon: FileText },
            { key: 'payments', label: 'Payments', icon: DollarSign },
            { key: 'quality', label: 'Quality Issues', icon: AlertCircle },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 whitespace-nowrap flex items-center gap-2 px-6 py-4 text-sm font-medium transition ${
                activeTab === tab.key
                  ? 'border-b-2 border-foreground text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Order Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Order Items */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Order Items</h3>
                <div className="space-y-3">
                  {order.order_items.map((item, index) => (
                    <div key={index} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{item.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.quantity} {item.unit} × ₹{parseFloat(item.unit_price).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <p className="font-semibold text-foreground">
                          ₹{(item.quantity * item.unit_price).toLocaleString('en-IN')}
                        </p>
                      </div>

                      {item.custom_fields && Object.keys(item.custom_fields).length > 0 && (
                        <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2">
                          {Object.entries(item.custom_fields).map(
                            ([key, value]) =>
                              value && (
                                <div key={key} className="text-sm">
                                  <span className="text-muted-foreground">{key.replace('_', ' ')}:</span>
                                  <span className="text-foreground/80 ml-1">{value}</span>
                                </div>
                              ),
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="border-t pt-6">
                <div className="max-w-md ml-auto space-y-2">
                  <div className="flex justify-between text-foreground/80">
                    <span>Subtotal:</span>
                    <span>₹{parseFloat(String(order.subtotal)).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-foreground/80">
                    <span>Tax ({order.tax_percentage}%):</span>
                    <span>₹{parseFloat(String(order.tax_amount)).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-foreground pt-2 border-t">
                    <span>Total:</span>
                    <span>₹{parseFloat(String(order.total_amount)).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Info */}
              {(order.expected_delivery_date || order.actual_delivery_date) && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Delivery Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {order.expected_delivery_date && (
                      <div>
                        <p className="text-sm text-muted-foreground">Expected Delivery</p>
                        <p className="text-foreground">{new Date(order.expected_delivery_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {order.actual_delivery_date && (
                      <div>
                        <p className="text-sm text-muted-foreground">Actual Delivery</p>
                        <p className="text-foreground">{new Date(order.actual_delivery_date).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {order.notes && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Notes</h3>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-foreground/80 whitespace-pre-wrap">{order.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-foreground">Invoices</h3>
                <button
                  onClick={() => setShowInvoiceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition text-sm"
                >
                  <Plus size={16} />
                  Add Invoice
                </button>
              </div>

              {invoices.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No invoices created yet</p>
              ) : (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div key={invoice.id} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-foreground">{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            invoice.payment_status === 'paid'
                              ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                              : invoice.payment_status === 'partial'
                                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                                : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                          }`}
                        >
                          {invoice.payment_status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Invoice Amount</p>
                          <p className="font-medium text-foreground">
                            ₹{parseFloat(String(invoice.total_amount)).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Paid</p>
                          <p className="font-medium text-green-600 dark:text-green-400">
                            ₹{parseFloat(String(invoice.paid_amount)).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Remaining</p>
                          <p className="font-medium text-red-600 dark:text-red-400">
                            ₹{parseFloat(String(invoice.remaining_amount)).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-foreground">Payments</h3>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition text-sm"
                >
                  <Plus size={16} />
                  Record Payment
                </button>
              </div>

              {payments.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No payments recorded yet</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div key={payment.id} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-foreground">
                            ₹{parseFloat(String(payment.amount_paid)).toLocaleString('en-IN')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </p>
                          {payment.payment_method && (
                            <p className="text-xs text-muted-foreground mt-1">via {payment.payment_method}</p>
                          )}
                        </div>
                        {payment.payment_reference && (
                          <span className="text-xs text-muted-foreground font-mono">{payment.payment_reference}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quality Issues Tab */}
          {activeTab === 'quality' && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-foreground">Quality Issues</h3>
                <button
                  onClick={() => setShowQualityIssueModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition text-sm"
                >
                  <Plus size={16} />
                  Report Issue
                </button>
              </div>

              {qualityIssues.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No quality issues reported</p>
              ) : (
                <div className="space-y-3">
                  {qualityIssues.map((issue) => (
                    <div key={issue.id} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-foreground">{issue.issue_type.replace('_', ' ')}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(issue.issue_date).toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            issue.severity === 'critical'
                              ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                              : issue.severity === 'high'
                                ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
                                : issue.severity === 'medium'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                                  : 'bg-muted text-foreground'
                          }`}
                        >
                          {issue.severity}
                        </span>
                      </div>
                      <p className="text-foreground/80 mb-2">{issue.issue_description}</p>
                      {Number(issue.impact_on_timeline) > 0 && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          ⏱️ Delayed by {issue.impact_on_timeline} days
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals would go here - simplified for now */}
      {showInvoiceModal && (
        <Modal maxWidth="md" fullHeight={false} onClose={() => setShowInvoiceModal(false)} className="rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4">Create Invoice</h3>
          <p className="text-muted-foreground mb-4">Invoice creation form would go here</p>
          <button
            onClick={() => setShowInvoiceModal(false)}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition"
          >
            Close
          </button>
        </Modal>
      )}

      {showPaymentModal && (
        <Modal maxWidth="md" fullHeight={false} onClose={() => setShowPaymentModal(false)} className="rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4">Record Payment</h3>
          <p className="text-muted-foreground mb-4">Payment recording form would go here</p>
          <button
            onClick={() => setShowPaymentModal(false)}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition"
          >
            Close
          </button>
        </Modal>
      )}

      {showQualityIssueModal && (
        <Modal
          maxWidth="md"
          fullHeight={false}
          onClose={() => setShowQualityIssueModal(false)}
          className="rounded-lg p-6"
        >
          <h3 className="text-xl font-bold mb-4">Report Quality Issue</h3>
          <p className="text-muted-foreground mb-4">Quality issue form would go here</p>
          <button
            onClick={() => setShowQualityIssueModal(false)}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition"
          >
            Close
          </button>
        </Modal>
      )}
    </div>
  );
};

export default VendorOrderDetailPage;
